import React, { useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, useMediaQuery, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import a1logo from '../../assets/images/a1_red_logo.png';
import { useHeaderFooterContext, headerFooterProviderDispatchAction } from '../../context/HeaderFooterProvider';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';

const useStyles = makeStyles(
  (theme) => ({
    root: {
      fontFamily: theme.typography.fontFamily,
      display: 'grid',
      gridTemplateColumns: `40px 1fr`,
      gridTemplateRows: `50px 60px`,
      gridTemplateAreas: `
        'top-nav top-nav'
        'logo bottom-nav'
        `,
      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: `1fr 150px 1216px 1fr`,
        gridTemplateRows: `50px 100px`,
        gridTemplateAreas: `
        'gtl logo top-nav gtr'
        'gbl logo bottom-nav gbr'
        `,
      },
      '& a': {
        display: 'inline-block',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'color .2s ease-out',
        cursor: 'pointer',
      },
    },
    gutterTop: {
      display: 'none',
      backgroundColor: theme.palette.background.dark,
      [theme.breakpoints.up('md')]: {
        display: 'block',
      },
    },
    gutterBottom: {
      display: 'none',
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid #d7d7d7`,
      [theme.breakpoints.up('md')]: {
        display: 'block',
      },
    },
    logo: {
      zIndex: 999,
      gridArea: 'logo',
      backgroundColor: theme.palette.background.paper,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingLeft: 30,
      borderBottom: `1px solid #d7d7d7`,
      '& img': {
        width: 40,
      },
      //},
      [theme.breakpoints.up('md')]: {
        marginLeft: 0,
        paddingLeft: 0,
        borderLeft: `1px solid ${theme.palette.background.default}`,
        borderRight: `1px solid ${theme.palette.background.default}`,
        borderBottom: `1px solid ${theme.palette.a1.border}`,
        '& img': {
          width: 'auto',
        },
      },
    },
    topNav: {
      gridArea: 'top-nav',
      display: 'flex',
      width: '100%',
      justifyContent: 'flex-start',
      color: theme.palette.common.white,
      backgroundColor: theme.palette.common.black,
      fontSize: 13,
      lineHeight: 'inherit',
      textTransform: 'uppercase',
      '& ul': {
        marginLeft: 20,
        paddingLeft: 0,
        textDecoration: 'none',
        listStyleType: 'none',
        color: '#bbb',
        marginTop: 18,
        '& li': {
          marginRight: 30,
          paddingBottom: 10,
          lineHeight: '18px',
          display: 'inline-block',
        },
        '& li:hover': {
          borderBottom: `4px solid ${theme.palette.a1.tooltipBackground}`,
        },
        '& li:first-of-type': {
          borderBottom: `4px solid ${theme.palette.a1.tooltipBackground}`,
          '& a': {
            color: theme.palette.common.white,
          },
        },
      },
      [theme.breakpoints.up('md')]: {
        lineHeight: '25px',
        fontSize: '0.75rem',
        paddingLeft: 5,
        '& ul': {
          marginLeft: 40,
          marginTop: 14,
        },
      },
    },
    bottomNavMobile: {
      gridArea: 'bottom-nav',
      borderBottom: `1px solid ${theme.palette.a1.border}`,
      backgroundColor: theme.palette.common.white,
      fontFamily: theme.typography.fontFamilySerif,
      fontSize: '1.25rem',
      color: theme.palette.text.primary,
      minWidth: '100%',
      [theme.breakpoints.up('smx')]: {
        display: 'none',
      },
      transition: [['all .8s ease'], '!important'],
      '& $mobileMenu': {
        position: 'absolute',
        left: 0,
        top: 110,
        width: '100%',
        '& $accordion': {
          backgroundColor: theme.palette.background.default,
          '& .MuiAccordionSummary-root': {
            height: 47,
            minHeight: [[47], '!important'],
            color: theme.palette.text.mobileMenuItem,
            backgroundColor: theme.palette.background.paper,
          },
          '& .MuiAccordionDetails-root': {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.mobileMenuItem,
            fontSize: '1.15rem',
            fontFamily: theme.typography.fontFamily,
            display: 'flex',
            flexDirection: 'column',
            padding: [[0, 0, 6, 24]],
            '& a': {
              display: 'flex',
              height: 46,
              alignItems: 'center',
            },
          },
        },
        '& > div:first-of-type::before': {
          content: [['none'], '!important'],
        },
      },
    },
    accordionNoContent: {
      backgroundColor: theme.palette.background.paper,
      height: 47,
      minHeight: [[47], '!important'],
      color: [[theme.palette.text.action], '!important'],
      padding: [[0, 0, 0, 16]],
      display: 'flex',
      alignItems: 'center',
    },
    bottomNav: {
      display: 'none',
      [theme.breakpoints.up('smx')]: {
        display: 'unset',
        gridArea: 'bottom-nav',
        backgroundColor: theme.palette.common.white,
        fontFamily: theme.typography.fontFamilySerif,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.a1.border}`,
        minWidth: '100%',
        '& > div': {
          position: 'relative',
          display: 'flex',
          justifyContent: 'stretch',
          flexWrap: 'nowrap',
          minHeight: '100%',
          alignItems: 'center',
          color: theme.palette.text.primary,
          [theme.breakpoints.up('smx')]: {
            justifyContent: 'flex-start',
            fontSize: 13,
            '& ul': {
              display: 'flex',
              marginLeft: 40,
              paddingLeft: 0,
              cursor: 'default',
              width: '100%',
              fontFamily: theme.typography.fontFamilySerif,
              textAlign: 'left',
              textDecoration: 'none',
              listStyleType: 'none',
              '& li': {
                //width: (props) => `calc(100% / ${props.subLevelOneItemsCount})`,
                marginRight: '1.25rem',
                lineHeight: 1,
                display: 'inline-block',
                whiteSpace: 'pre',
                '& small': {
                  float: 'left',
                  fontFamily: theme.typography.fontFamily,
                  fontSize: '0.75rem',
                  width: '100%',
                  lineHeight: 1.16667,
                  fontWeight: 400,
                  color: theme.palette.text.small,
                },
              },
            },
          },
          [theme.breakpoints.up('md')]: {
            fontSize: '1.125rem',
            minWidth: 810,
            '& ul': {
              position: 'absolute',
              '& li': {
                lineHeight: '22px',
                //width: (props) =>
                //  `calc(770px / ${props.subLevelOneItemsCount})`,
              },
            },
          },
        },
      },
    },
    bottomNavDetails: {
      zIndex: 999,
      [theme.breakpoints.up('smx')]: {
        //paddingLeft: 5,
        paddingBottom: 20,
        position: 'absolute',
        display: 'flex',
        flexDirection: 'row',
        height: 'auto',
        minHeight: (props) =>
          props?.selectedIndex?.sub > 0 && props?.selectedIndex?.hasSubItems
            ? 400
            : 'unset',
        top: 110,
        backgroundColor: theme.palette.background.paper,
        width: '100%',
        left: 0,
        paddingLeft: (props) => (props.selectedIndex ? `calc(20px + 40px + ${props.selectedIndex.gutterWidth}px)` : 0),
        justifyContent: 'flex-start',
        transition: 'height .15s ease',
      },
      [theme.breakpoints.up('md')]: {
        top: 150,
        //justifyContent: 'center',
        paddingLeft: (props) => (props.selectedIndex ? `calc(90px + 99px + ${props.selectedIndex.gutterWidth}px)` : 0),
      },
    },
    bottomNavDetailsCol: {
      [theme.breakpoints.up('smx')]: {
        width: 170,
        marginRight: 25,
        marginTop: 20,
        marginLeft: 20,
      },
      [theme.breakpoints.up('md')]: {
        marginLeft: 0,
      },
    },
    detailsGroup: {
      display: 'none',
      [theme.breakpoints.up('smx')]: {
        display: 'flex',
        //display: (props) =>
        //  props.selectedIndex && props.selectedIndex.sub > 0 ? 'flex' : 'none',
        flexDirection: 'column',
        '& a': {
          fontFamily: theme.typography.fontFamily,
          lineHeight: '20px',
          fontSize: 14,
          marginBottom: 6,
        },
        '& a:first-of-type': {
          fontFamily: theme.typography.fontFamilySerif,
          fontSize: 18,
          lineHeight: '22px',
          marginTop: 10,
        },
        '& a:nth-of-type(2)': {
          marginTop: 15,
        },
        [theme.breakpoints.down('smx')]: {
          '& a:not(first-of-type)': {
            display: 'none',
          },
        },
      },
    },
    menuIndicator: {
      display: 'none',
      [theme.breakpoints.up('smx')]: {
        transition: 'left 150ms ease-in-out 0s',
        position: 'relative',
        display: 'flex',
        alignSelf: 'flex-end',
        bottom: 0,
        borderBottom: `4px solid ${theme.palette.primary.main}`,
        left: (props) => (props.selectedIndex ? `calc(40px + ${props.selectedIndex.left}px)` : 0),
        width: (props) => (props.selectedIndex ? props.selectedIndex.width : 0),
      },
      [theme.breakpoints.up('md')]: {
        /*left: (props) =>
          `calc(40px + (${Math.max(
            0,
            props.selectedIndex ? props.selectedIndex.sub - 1 : 0
          )} * (770px / ${props.subLevelOneItemsCount}) + ${
            props.selectedIndex && props.selectedIndex.sub === 6 ? 20 : 0
          }px)`,
        width: (props) =>
          `calc(${
            props.selectedIndex && props.selectedIndex.sub > 0 ? 1 : 0
          } * ${
            props.selectedIndex && props.selectedIndex.sub === 5
              ? 140
              : 770 / props.subLevelOneItemsCount
          }px  - 20px)`,*/
      },
    },
    topNavShaded: {
      color: '#999',
    },
    topNavLogin: {
      width: [[90], '!important'],
    },
    topNavColored: {
      color: theme.palette.text.action,
      [theme.breakpoints.up('md')]: {
        width: [[130], '!important'],
      },
    },
    accordion: {
      '&::before': {
        backgroundColor: theme.palette.background.paper,
      },
      '&.MuiAccordion-root.Mui-expanded': {
        margin: 0,
      },
    },
    mobileMenu: {
      zIndex: 999,
      display: (props) => (props.expandedMobile === false ? 'none' : 'unset'),
    },
    mobileHandle: {
      width: 45,
      height: 45,
      padding: 12,
      position: 'absolute',
      right: 20,
      top: (props) => (props.expandedMobile ? 65 : 60),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& svg': {
        transform: 'scale(1.4)',
      },
      backgroundColor: (props) => (props.expandedMobile ? theme.palette.background.default : 'transparent'),
      borderTop: (props) => (props.expandedMobile ? `1px solid ${theme.palette.a1.border}` : 'none'),
      borderLeft: (props) => (props.expandedMobile ? `1px solid ${theme.palette.a1.border}` : 'none'),
      borderRight: (props) => (props.expandedMobile ? `1px solid ${theme.palette.a1.border}` : 'none'),
      [theme.breakpoints.up('smx')]: {
        display: 'none',
      },
    },
    closeMenuIcon: {},
  }),
  { name: 'DavaHeader' }
);

const Header = () => {
  const theme = useTheme();
  const matchsmxUp = useMediaQuery(theme.breakpoints.up('smx'));
  //const [subLevelOneItemsCount, setsubLevelOneItemsCount] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState({
    main: 0,
    sub: 0,
    left: 0,
    paddingLeft: 0,
    width: 0,
  });
  const [expandedMobile, setExpandedMobile] = React.useState(false);
  const styles = useStyles({
    selectedIndex,
    expandedMobile,
  });
  const { dataLoading, headerJson, headerFooterCallback } =
    useHeaderFooterContext();

  const accIconProps = {
    size: 'small',
    disableFocusRipple: true,
    color: 'secondary',
    disableRipple: true,
  };

  useEffect(() => {
    if (!headerJson && !dataLoading) headerFooterCallback(headerFooterProviderDispatchAction.FETCH_DATA);
  }, [dataLoading, headerJson, headerFooterCallback]);

  const handleSetSelected = (
    parentIndex,
    subMenuIndex,
    targetElement,
    gutterRef,
    hasSubItems
  ) => {
    setSelectedIndex({
      main: parentIndex,
      sub: subMenuIndex,
      left: targetElement.offsetLeft,
      width: targetElement.offsetWidth,
      gutterWidth: gutterRef.current.offsetWidth,
      hasSubItems: hasSubItems,
    });
  };

  const handleUnsetSelected = () => {
    setSelectedIndex({ ...selectedIndex, sub: 0 });
  };

  const buildSubMenuColums = (data) => {
    var columns = data.map((column, idx) => {
      //console.log('group:', column.groups);
      return (
        <div key={idx} className={styles.bottomNavDetailsCol}>
          {menuGroup(column.groups)}
        </div>
      );
    });
    return columns;
  };

  const menuGroup = (data) =>
    data.map((item) => (
      <div key={item.key} className={styles.detailsGroup}>
        <a href={item.url}>{item.name}</a>
        {subMenuItems(item)}
      </div>
    ));

  const subMenuItems = (data) => data.items?.map((item) => <a href={item.url}>{item.name}</a>);

  let levelTwoMenu;

  if (headerJson && selectedIndex && selectedIndex.sub > 0) {
    const levelTwo = headerJson?.header?.menu[selectedIndex.main].subMenu[selectedIndex.sub - 1]?.subMenuDetails?.map((column, columnIndex) => {
      let columns = {
        key: `${columnIndex}`,
      };

      // groups
      columns.groups = [];
      column.map((group, groupIndex) => {
        group.items?.forEach((item, itemIndex) => {
          item.key = `${columnIndex}${groupIndex}${itemIndex}`;
        });

        let mappedGroup = [
          {
            key: `${columnIndex}${groupIndex}0`,
            name: group.name,
            url: group.url,
            items: group.items,
          },
        ];
        columns.groups = columns.groups.concat(mappedGroup);

        return column.groups;
      });

      return columns;
    });
    //console.log('levelTwo:', levelTwo);
    levelTwoMenu = levelTwo ? buildSubMenuColums(levelTwo) : undefined;
  }

  const handleCloseMobile = () => {
    setExpandedMobile(false);
  };

  const handleOpenMobile = () => {
    setExpandedMobile(true);
  };

  const gutterBottomRef = React.createRef();

  const subMenu = (itemIndex) => (
    <>
      <div>
        <ul>
          {headerJson?.header?.menu[itemIndex]?.subMenu?.map((subMenuItem, subMenuIndex) => {
            const desktopDisplayData = subMenuItem?.display?.filter((d) => d.platform === 'desktop');
            let fullItemName = subMenuItem.name;
            let elementProps = {};
            if (desktopDisplayData && desktopDisplayData.length > 0 && matchsmxUp) {
              const nameChunks = subMenuItem.name.split(' ');
              const wordBreaks = desktopDisplayData[0].lineBreakAfterNth;
              if (wordBreaks) {
                fullItemName = '';
                nameChunks.forEach((chunk, index) => {
                  fullItemName += chunk;
                  if (wordBreaks.some((x) => x === index + 1)) {
                    fullItemName += '\n';
                  } else {
                    fullItemName += ' ';
                  }
                });
              }

              const color = desktopDisplayData[0].color;
              if (color === 'hint') {
                elementProps.style = { color: theme.palette.text.action };
              } else if (color === 'disabled') {
                elementProps.style = { color: '#999' };
              }
            }

            // is it a link?
            const linkUrl = subMenuItem.url;
            if (linkUrl) {
              if (!elementProps.style) {
                elementProps.style = {};
              }
              elementProps.style.cursor = 'pointer';
            }

            // does it have any items?
            const hasSubItems = subMenuItem.subMenuDetails && subMenuItem.subMenuDetails.length > 0;

            const newLineIndex = fullItemName.indexOf('\n');
            const topItemName = newLineIndex > 0 ? fullItemName.substr(0, newLineIndex) + '\n' : fullItemName;
            const bottomItemName = newLineIndex > 0 ? fullItemName.substr(newLineIndex + 1) : null;

            return (
              <li
                {...elementProps}
                id={`subMenuItem${itemIndex}${subMenuIndex}`}
                onMouseOver={(e) => {
                  if (subMenuIndex + 1 === selectedIndex.sub) {
                    // prevent rerendering on click events
                    return;
                  }
                  handleSetSelected(itemIndex, subMenuIndex + 1, e.target, gutterBottomRef, hasSubItems);
                }}
                key={subMenuItem.name}
              >
                {linkUrl && (
                  <a href={linkUrl} target='_blank' rel='noreferrer noopener'>
                    {topItemName}
                    {bottomItemName && <small>{bottomItemName}</small>}
                  </a>
                )}
                {!linkUrl && (
                  <>
                    {topItemName}
                    {bottomItemName && <small>{bottomItemName}</small>}
                  </>
                )}
              </li>
            );
          })}
          {/* {itemIndex === 0 && headerJson?.header?.menu[itemIndex]?.subMenu && (
            <li
              onMouseOver={handleUnsetSelected}
              className={clsx(styles.topNavShaded, styles.topNavLogin)}
              key='Moj A1 Prijavi se'
            >
              <a href='https://moj.a1.si'>
                {'Moj A1\n'}
                <small>Prijavi se</small>
              </a>
            </li>
          )} */}
        </ul>
        <span className={styles.menuIndicator}></span>
      </div>
      <nav className={styles.bottomNavDetails}>{levelTwoMenu}</nav>
    </>
  );

  const HeaderMenu = () => {
    //const styles = useStyles();
    const styles = useStyles({
      selectedIndex,
      expandedMobile,
    });

    const [expanded, setExpanded] = React.useState(false);

    const handleAccordion = (accordionName) => (event, isExpanded) => {
      setExpanded(isExpanded ? accordionName : false);
    };

    const MobileAccordion = (item) => (
      <Accordion
        key={item.name}
        className={styles.accordion}
        square
        elevation={0}
        expanded={expanded === item.name}
        onChange={handleAccordion(item.name)}
      >
        <AccordionSummary
          IconButtonProps={accIconProps}
          expandIcon={<ExpandMoreIcon />}
        >
          {item.name}
        </AccordionSummary>
        <AccordionDetails>
          {item.subMenuDetails?.map((group) =>
            group.map((detail) => (
              <a key={detail.name} href={detail.url}>
                {detail.name}
              </a>
            ))
          )}
        </AccordionDetails>
      </Accordion>
    );

    return (
      <>
        <nav className={styles.topNav}>
          <ul>
            {headerJson?.header?.menu?.map((menuItem, index) => (
              <li key={index}>
                <a href={menuItem.url}>{menuItem.name}</a>
              </li>
            ))}
          </ul>
        </nav>
        <nav onMouseLeave={handleUnsetSelected} className={styles.bottomNav}>
          {subMenu(selectedIndex.main)}
        </nav>

        {!matchsmxUp && (
          <>
            <nav className={styles.bottomNavMobile}>
              <div className={styles.mobileMenu}>
                {headerJson?.header?.menu[selectedIndex.main]?.subMenu?.map(
                  (item) => {
                    //console.log('item.url:', item.url);
                    if (item.url) {
                      return (
                        <div className={styles.accordionNoContent}>
                          <a
                            href={item.url}
                            target='_blank'
                            rel='noreferrer noopener'
                          >
                            {item.name}
                          </a>
                        </div>
                      );
                    } else {
                      return MobileAccordion(item);
                    }
                  }
                )}
              </div>
            </nav>
            <div className={styles.mobileHandle}>
              {headerJson?.header?.menu[selectedIndex.main] && (
                <div>
                  {!expandedMobile ? (
                    <MenuIcon fontSize='large' color='secondary' onClick={handleOpenMobile} />
                  ) : (
                    <CloseIcon className={styles.closeMenuIcon} color='primary' onClick={handleCloseMobile} />
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className={styles.root}>
      <div className={styles.logo}>
        <a href='/'>
          <img src={a1logo} alt='A1 Slovenija' title='A1 Slovenija' />
        </a>
      </div>
      <HeaderMenu />
      <div className={styles.gutterTop} styles={{ gridArea: 'gtl' }} />
      <div className={styles.gutterTop} styles={{ gridArea: 'gtr' }} />
      <div className={styles.gutterBottom} styles={{ gridArea: 'gbl' }} ref={gutterBottomRef} />
      <div className={styles.gutterBottom} styles={{ gridArea: 'gbr' }} />
    </div>
  );
};

export default Header;
